// @flow
import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Layout, Menu, Row, Icon, Popover, Button } from "antd";
import Media from "react-media";
import FaFacebookOfficial from "react-icons/lib/fa/facebook-official";
import FaTwitter from "react-icons/lib/fa/twitter";
import FaYoutubePlay from "react-icons/lib/fa/youtube-play";
import MdMenu from "react-icons/lib/md/menu";
import * as actions from "../../actions";
import type { State } from "../../types/store";
import type { Entities } from "../../types/entities";
import type { Language } from "../../reducers/language";
import type { User } from "../../reducers/auth";
import "./nav.css";

const SubMenu = Menu.SubMenu;
const MenuItemGroup = Menu.ItemGroup;
const { Header } = Layout;
const logo = require("../../images/logo.png");

type Props = {
  entities: Entities,
  selectedLanguage: ?Language,
  onSelectLanguage: Function,
  onHomeClicked: Function,
  user: User,
  isFetching: boolean,
  onLogout: Function
};

const Nav = ({
  entities,
  selectedLanguage,
  onSelectLanguage,
  onHomeClicked,
  user,
  isFetching,
  onLogout
}: Props) => {
  const handleClick = e => {
    if (e.key.includes("flag")) {
      const code = e.key.substr(5, 5);
      const language = entities.byId[code];
      onSelectLanguage(language);
    }
  };

  const getLanguageIcon = (id: string): string => {
    const code = id.substr(3, 2);
    return code;
  };

  const languages = entities.all.map(_id => {
    const { id, name } = entities.byId[_id];
    return (
      <Menu.Item key={`flag:${id}`}>
        <div className="flag-wrapper">
          <img
            className="flag"
            src={`/flags/${getLanguageIcon(id)}.png`}
            alt={name}
          />
          <span className="flag-name">{name}</span>
        </div>
      </Menu.Item>
    );
  });

  const links = [
    <Menu.Item key="link:1">
      <a href="http://www.casece.com" target="_blank" rel="noopener noreferrer">
        Web Site
      </a>
    </Menu.Item>,
    <Menu.Item key="link:2">
      <a
        href="http://www.caseordersystem.com"
        target="_blank"
        rel="noopener noreferrer"
      >
        Brochure Bank
      </a>
    </Menu.Item>,
    <Menu.Item key="link:3">
      <a
        href="http://www.casecetools.com/Press-Kit/web_html/Catalog"
        target="_blank"
        rel="noopener noreferrer"
      >
        Press Kit
      </a>
    </Menu.Item>
  ];

  const social = [
    <Menu.Item key="setting:1">
      <a
        href="https://www.facebook.com/caseconstructionequipment.italia"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FaFacebookOfficial className="social-icon" />Facebook
      </a>
    </Menu.Item>,
    <Menu.Item key="setting:2">
      <a
        href="https://twitter.com/CaseCE_EU"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FaTwitter className="social-icon" />twitter
      </a>
    </Menu.Item>,
    <Menu.Item key="setting:4">
      <a
        href="http://www.youtube.com/caseatwork"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FaYoutubePlay className="social-icon" />youtube
      </a>
    </Menu.Item>
  ];

  return (
    <Header className="main-nav">
      <Row type="flex" justify="space-between" align="middle">
        <Link className="logo-link" to="/home" onClick={onHomeClicked}>
          <img src={logo} alt="" className="logo" />
        </Link>
        <div className="navbar">
          {user && (
            <Media query="(max-width: 768px)">
              {matches =>
                matches ? (
                  <p />
                ) : (
                  <p className="welcome">Welcome {user.firstName}</p>
                )
              }
            </Media>
          )}
          {user && (
            <Button
              className="logout-button"
              onClick={onLogout}
              loading={isFetching}
            >
              Log out
            </Button>
          )}
          <Media query="(max-width: 768px)">
            {matches =>
              matches ? (
                // menu mobile
                <Popover
                  overlayClassName="popover-menu-mobile"
                  placement="bottomRight"
                  content={
                    <Menu className="menu-mobile" onClick={handleClick}>
                      {selectedLanguage && (
                        <MenuItemGroup key="l1" title="Languages">
                          {languages.map(item => item)}
                        </MenuItemGroup>
                      )}
                      <MenuItemGroup key="g1" title="Links">
                        {links.map(item => item)}
                      </MenuItemGroup>
                      <MenuItemGroup key="g2" title="Social">
                        {social.map(item => item)}
                      </MenuItemGroup>
                    </Menu>
                  }
                  trigger="click"
                >
                  <Button className="menu-btn">
                    <MdMenu />
                  </Button>
                </Popover>
              ) : (
                // menu desktop
                <Menu
                  className="menu"
                  theme="light"
                  mode="horizontal"
                  onClick={handleClick}
                >
                  {selectedLanguage && (
                    <SubMenu
                      title={
                        <div className="flag-wrapper">
                          <img
                            className="flag"
                            src={`/flags/${getLanguageIcon(
                              selectedLanguage.id
                            )}.png`}
                            alt={selectedLanguage.name}
                          />
                        </div>
                      }
                    >
                      {languages.map(item => item)}
                    </SubMenu>
                  )}
                  <SubMenu
                    title={
                      <span>
                        <Icon type="link" />Links
                      </span>
                    }
                  >
                    {links.map(item => item)}
                  </SubMenu>
                  <SubMenu
                    title={
                      <span>
                        <Icon type="share-alt" />Social Media
                      </span>
                    }
                  >
                    {social.map(item => item)}
                  </SubMenu>
                </Menu>
              )
            }
          </Media>
        </div>
      </Row>
    </Header>
  );
};

function mapStateToProps(state: State) {
  const { entities, selectedLanguage } = state.language;
  const { user, isFetching } = state.auth;
  return {
    entities,
    selectedLanguage,
    user,
    isFetching
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onLogout: bindActionCreators(actions.logout, dispatch),
    onSelectLanguage: bindActionCreators(actions.selectLanguage, dispatch),
    onHomeClicked: bindActionCreators(actions.getMainFilters, dispatch)
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(Nav);

Nav.propTypes = {
  entities: PropTypes.object,
  selectedLanguage: PropTypes.object,
  user: PropTypes.object,
  isFetching: PropTypes.bool.isRequired,
  onLogout: PropTypes.func.isRequired,
  onSelectLanguage: PropTypes.func.isRequired,
  onHomeClicked: PropTypes.func.isRequired
};



// WEBPACK FOOTER //
// ./src/components/nav/index.js